import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  useTheme,
  alpha,
  Skeleton,
  CircularProgress,
  Button
} from '@mui/material';
import { 
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { getScriptContent, updateScriptTags } from '../services/scriptService';
import { createScreenplayAnalysis, getScriptScreenplayAnalysis } from '../services/screenplayAnalysisService';
import { exportScreenplayToPdf } from '../utils/screenplayPdfExport';
import SimpleScreenplayViewer from './SimpleScreenplayViewer';

/**
 * ScriptViewer Component
 * 
 * Displays script content in either text or PDF format with controls for navigation,
 * downloading, printing, and tagging.
 * 
 * @param {Object} script - The script object containing metadata
 * @param {Function} onBack - Callback function when back button is clicked
 * @param {Function} onAnalyzeScript - Callback function when analyze script button is clicked
 */
const ScriptViewer = ({ script, onBack, onAnalyzeScript }) => {
  const theme = useTheme();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [scriptTags, setScriptTags] = useState([]);
  const [coverPage, setCoverPage] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const pollingIntervalRef = useRef(null);

  const stopAnalysisStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  const [canWriteTags, setCanWriteTags] = useState(true);
  const [canAnalyze, setCanAnalyze] = useState(true);

  // Fetch script content when script changes
  useEffect(() => {
    if (!script?._id) return;

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getScriptContent(script._id, 'latest', script.projectId);
        
        if (response.type === 'pdf') {
          setContentType('pdf');
          setContent(response);
          setCoverPage(null);
        } else {
          setContentType('text');
          setContent(response.content || response);
          // Load saved tags if they exist
          if (response.tags) {
            setScriptTags(response.tags);
          }
          setCoverPage(response.coverPage || null);
        }
      } catch (err) {
        console.error('Error fetching script content:', err);
        setError(err.message || 'Failed to load script content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [script?._id]);

  // Check for existing analysis when component mounts
  useEffect(() => {
    if (!script?._id) return;
    
    const checkExistingAnalysis = async () => {
      try {
        if (!canAnalyze) return;
        const analysis = await getScriptScreenplayAnalysis(script._id);
        
        // If an analysis exists, start polling for status updates
        if (analysis) {
          setAnalysisStatus(analysis.status);
          
          // If analysis is still in progress, start polling
          if (analysis.status === 'pending' || analysis.status === 'processing') {
            setAnalyzeLoading(true);
            startAnalysisStatusPolling(script._id);
          }
        }
      } catch (err) {
        // If permission error, disable analysis UI
        const status = err?.response?.status || err?.status;
        if (status === 403) {
          setCanAnalyze(false);
          setAnalyzeLoading(false);
          stopAnalysisStatusPolling();
          return;
        }
        // No existing analysis found — this is expected for new scripts
      }
    };
    
    checkExistingAnalysis();
    
    // Cleanup: clear polling interval when script changes or component unmounts
    return () => {
      stopAnalysisStatusPolling();
    };
  }, [script?._id, canAnalyze]);

  /**
   * Handles downloading the script content
   */
  const handleDownload = async () => {
    try {
      if (contentType === 'pdf' && content?.url) {
        // For PDF files, create a temporary link and click it
        const link = document.createElement('a');
        link.href = content.url;
        link.download = script.title || 'script.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (contentType === 'text' && content) {
        // For Fountain text content, export to Hollywood-format PDF
        if (script.type === 'screenplay' || script.format === 'fountain') {
          // Try to parse title page from the Fountain content
          let titlePage = null;
          const titleMatch = content.match(/^Title:\s*(.+)$/im);
          const creditMatch = content.match(/^Credit:\s*(.+)$/im);
          const authorMatch = content.match(/^Author:\s*(.+)$/im);
          const sourceMatch = content.match(/^Source:\s*(.+)$/im);
          const draftMatch = content.match(/^Draft date:\s*(.+)$/im);
          const contactMatch = content.match(/^Contact:\s*(.+)$/im);
          if (titleMatch || authorMatch || creditMatch) {
            titlePage = {
              title: titleMatch?.[1]?.trim(),
              credit: creditMatch?.[1]?.trim(),
              author: authorMatch?.[1]?.trim(),
              source: sourceMatch?.[1]?.trim(),
              draft: draftMatch?.[1]?.trim(),
              contact: contactMatch?.[1]?.trim(),
            };
          }
          exportScreenplayToPdf({ content, title: script.title, titlePage });
        } else {
          // Non-screenplay text: download as .txt
          const blob = new Blob([content], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${script.title || 'script'}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Error downloading script:', err);
      setError('Failed to download script: ' + (err.message || 'Unknown error'));
    }
  };

  /**
   * Handles printing the script content
   */
  const viewerRef = useRef(null);

  const handlePrint = () => {
    if (contentType === 'pdf' && content?.url) {
      // For PDFs, open in a new window and print
      const printWindow = window.open(content.url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        setError('Pop-up blocked. Please allow pop-ups to print.');
      }
    } else {
      // For text content, use the dedicated print method from SimpleScreenplayViewer
      if (viewerRef.current && typeof viewerRef.current.printScreenplay === 'function') {
        viewerRef.current.printScreenplay();
      } else {
        window.print();
      }
    }
  };

  /**
   * Handles updating script tags
   */
  const handleTagsUpdate = async (newTags) => {
    try {
      // Save the updated tags to the server
      await updateScriptTags(script._id, script.projectId, newTags);
      // Update local state only after successful server update
      setScriptTags(newTags);
      // Notify other parts of the app (e.g., Schedule) that tags changed
      try {
        window.dispatchEvent(new CustomEvent('scriptTagsUpdated', {
          detail: { projectId: script.projectId, scriptId: script._id }
        }));
      } catch (e) {
        // Fail silently if CustomEvent isn't available
      }
    } catch (err) {
      console.error('Error saving tags:', err);
      // If permission denied, lock the UI into read-only tagging
      if (err?.response?.status === 403) {
        setCanWriteTags(false);
      }
      throw err; // Propagate error to SimpleScreenplayViewer
    }
  };

  /**
   * Handles script analysis request
   */
  const handleAnalyzeScript = async () => {
    if (!script?._id || !script.projectId || !canAnalyze) return;
    
    try {
      setAnalyzeLoading(true);
      await createScreenplayAnalysis(script._id, script.projectId);
      
      // Start polling for analysis status
      startAnalysisStatusPolling(script._id);
      
      // If onAnalyzeScript callback is provided, call it to switch to the analysis tab
      if (typeof onAnalyzeScript === 'function') {
        onAnalyzeScript();
      }
    } catch (err) {
      console.error('Error starting screenplay analysis:', err);
      setError('Failed to start screenplay analysis: ' + (err.message || 'Unknown error'));
      setAnalyzeLoading(false);
    }
  };

  /**
   * Starts polling for analysis status updates
   */
  const startAnalysisStatusPolling = (scriptId) => {
    // Clear any existing polling interval
    stopAnalysisStatusPolling();
    
    // Set initial status to processing
    setAnalysisStatus('processing');
    
    // Define the polling function
    const checkAnalysisStatus = async () => {
      try {
        const analysis = await getScriptScreenplayAnalysis(scriptId);
        
        // Update the status
        if (analysis && analysis.status) {
          setAnalysisStatus(analysis.status);
        }
        
        // If analysis is completed or has an error, stop polling
        if (analysis?.status === 'completed' || analysis?.status === 'failed') {
          stopAnalysisStatusPolling();
          setAnalyzeLoading(false);
        }
      } catch (err) {
        console.error('Error checking analysis status:', err);
        // Don't stop polling on error, just log it
      }
    };
    
    // Run immediately
    checkAnalysisStatus();
    
    // Set up polling interval (every 3 seconds)
    pollingIntervalRef.current = setInterval(checkAnalysisStatus, 3000);
  };

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      stopAnalysisStatusPolling();
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        height="100%" 
        p={4}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Skeleton 
            variant="rectangular" 
            width={280} 
            height={24} 
            sx={{ borderRadius: 1, mb: 2 }}
          />
          <Skeleton 
            variant="rectangular" 
            width={200} 
            height={16} 
            sx={{ borderRadius: 1, mb: 3 }}
          />
          <CircularProgress size={32} sx={{ color: 'text.secondary' }} />
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ mt: 1.5, display: 'block' }}
          >
            Loading script...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4,
          m: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100% - 32px)',
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0,0,0,0.2)'
            : '0 1px 4px rgba(0,0,0,0.08)'
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 1, 
            fontWeight: 600,
            color: theme.palette.error.main 
          }}
        >
          Error Loading Script
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center"
          sx={{ maxWidth: 320 }}
        >
          {error}
        </Typography>
        <Button
          onClick={onBack}
          startIcon={<BackIcon />}
          sx={{ mt: 3 }}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  // No content state
  if (!content) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4,
          m: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100% - 32px)',
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0,0,0,0.2)'
            : '0 1px 4px rgba(0,0,0,0.08)'
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 1, 
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          No Content Available
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center"
          sx={{ maxWidth: 320 }}
        >
          This script doesn't have any content to display.
        </Typography>
        <Button
          onClick={onBack}
          startIcon={<BackIcon />}
          sx={{ mt: 3 }}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Content area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {contentType === 'pdf' ? (
          <Box sx={{ 
            height: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.5)
              : '#fafbfc',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0,0,0,0.2)'
              : '0 1px 4px rgba(0,0,0,0.06)',
            '& iframe': {
              border: 'none',
              width: '100%',
              height: '100%',
              borderRadius: 0
            }
          }}>
            {content?.url ? (
              <iframe src={content.url} title="Script PDF" />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1">PDF not available</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.5)
                : '#fafbfc',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.2)'
                : '0 1px 4px rgba(0,0,0,0.06)'
            }}
          >
            <SimpleScreenplayViewer
              ref={viewerRef}
              content={content}
              coverPage={coverPage}
              onBack={onBack}
              onUpdateTags={handleTagsUpdate}
              scriptId={script._id}
              projectId={script.projectId}
              initialTags={scriptTags}
              disabledTagging={!canWriteTags}
              scriptTitle={script.title}
              showPrintButton
              onPrint={handlePrint}
              onDownload={handleDownload}
              onAnalyze={handleAnalyzeScript}
              canAnalyze={canAnalyze}
              analyzeLoading={analyzeLoading}
              analysisStatus={analysisStatus}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ScriptViewer;
